import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '@/components/ui';
import { ordersService } from '@/services';
import { ApiError } from '@/services/api';
import type { Order, OrderFulfillmentStatus, OrderStatus } from '@/types/order';
import { formatPrice } from '@/utils/format';
import { allowedNextFulfillmentStatuses } from '@/utils/orderFulfillment';
import { operationalOrderStatusLabel } from '@/utils/orderOperationalDisplay';

function statusLabel(status: OrderStatus, t: (key: string) => string): string {
  const key = `orders.status_${status}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

export function AdminOrdersPage(): JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const [selectionFulfillment, setSelectionFulfillment] = useState<
    Record<string, OrderFulfillmentStatus | ''>
  >({});
  const [selectionPayment, setSelectionPayment] = useState<Record<string, OrderStatus | ''>>({});
  const [commentFulfillment, setCommentFulfillment] = useState<Record<string, string>>({});
  const [commentPayment, setCommentPayment] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['admin', 'orders'],
    queryFn: () => ordersService.getAdminOrders(),
  });

  const updateFulfillmentMutation = useMutation({
    mutationFn: ({
      id,
      fulfillmentStatus,
      comment,
    }: {
      id: string;
      fulfillmentStatus: OrderFulfillmentStatus;
      comment?: string;
    }) => ordersService.updateOrderFulfillment(id, fulfillmentStatus, comment),
    onSuccess: async (_, variables) => {
      setFeedback(t('adminOrders.updatedFulfillment'));
      setErrorMsg(null);
      setCommentFulfillment((c) => ({ ...c, [variables.id]: '' }));
      setSelectionFulfillment((s) => ({ ...s, [variables.id]: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => {
      setFeedback(null);
      setErrorMsg(e instanceof ApiError ? e.message : t('common.error'));
    },
  });

  const updatePaymentMutation = useMutation({
    mutationFn: ({ id, status, comment }: { id: string; status: OrderStatus; comment?: string }) =>
      ordersService.updateOrderStatus(id, status, comment),
    onSuccess: async (_, variables) => {
      setFeedback(t('adminOrders.updatedPayment'));
      setErrorMsg(null);
      setCommentPayment((c) => ({ ...c, [variables.id]: '' }));
      setSelectionPayment((s) => ({ ...s, [variables.id]: '' }));
      await queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => {
      setFeedback(null);
      setErrorMsg(e instanceof ApiError ? e.message : t('common.error'));
    },
  });

  const handleApplyFulfillment = (order: Order) => {
    const next = selectionFulfillment[order.id];
    if (!next || next === order.fulfillmentStatus) return;
    if (
      !window.confirm(
        t('adminOrders.confirmFulfillment', {
          id: order.id.slice(0, 8),
          status: operationalOrderStatusLabel(next, t),
        }),
      )
    ) {
      return;
    }
    updateFulfillmentMutation.mutate({
      id: order.id,
      fulfillmentStatus: next,
      comment: commentFulfillment[order.id],
    });
  };

  const handleApplyPayment = (order: Order) => {
    const next = selectionPayment[order.id];
    if (!next || next === order.status) return;
    if (
      !window.confirm(
        t('adminOrders.confirmChange', {
          id: order.id.slice(0, 8),
          status: statusLabel(next, t),
        }),
      )
    ) {
      return;
    }
    updatePaymentMutation.mutate({
      id: order.id,
      status: next,
      comment: commentPayment[order.id],
    });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    const message = error instanceof ApiError ? error.message : t('adminOrders.loadError');
    return (
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4 text-red-800">{message}</div>
      </div>
    );
  }

  const list = orders ?? [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('adminOrders.title')}</h1>

      {feedback && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 font-sans text-sm text-green-800">
          {feedback}
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 font-sans text-sm text-red-800">
          {errorMsg}
        </div>
      )}

      {list.length === 0 ? (
        <p className="font-sans text-brand-700">{t('adminOrders.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-brand-700/20 bg-surface shadow-sm">
          <table className="min-w-full font-sans text-sm">
            <thead className="bg-brand-100 text-left text-brand-700">
              <tr>
                <th className="px-3 py-2 font-semibold">{t('orders.orderIdColumn')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.buyer')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.paymentColumn')}</th>
                <th className="px-3 py-2 font-semibold">{t('orders.fulfillmentColumn')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.totalCop')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.date')}</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-100">
              {list.map((order: Order) => {
                const nextFulfillment = allowedNextFulfillmentStatuses(order.fulfillmentStatus);
                const selFul = selectionFulfillment[order.id] ?? '';
                const busyFul =
                  updateFulfillmentMutation.isPending && updateFulfillmentMutation.variables?.id === order.id;
                const selPay = selectionPayment[order.id] ?? '';
                const busyPay =
                  updatePaymentMutation.isPending && updatePaymentMutation.variables?.id === order.id;
                const pendingPayment = order.status === 'PAGO_PENDIENTE';
                const paymentOptions: OrderStatus[] = ['PAGADA', 'CANCELADA'];

                return (
                  <tr key={order.id} className="hover:bg-brand-100/50">
                    <td className="px-3 py-2 font-mono text-xs text-brand-950">{order.id.slice(0, 8)}…</td>
                    <td className="px-3 py-2 text-brand-950">
                      {order.buyer.displayName || order.buyer.email}
                      <span className="block text-xs text-brand-700">{order.buyer.email}</span>
                    </td>
                    <td className="min-w-[200px] px-3 py-2 align-top">
                      <p className="mb-1 font-medium text-brand-950">{statusLabel(order.status, t)}</p>
                      {pendingPayment ? (
                        <div className="flex max-w-[240px] flex-col gap-1">
                          <select
                            className="rounded border border-brand-700/35 bg-surface px-2 py-1 text-brand-950"
                            value={selPay}
                            onChange={(e) =>
                              setSelectionPayment((s) => ({
                                ...s,
                                [order.id]: (e.target.value || '') as OrderStatus | '',
                              }))
                            }
                            disabled={busyPay}
                          >
                            <option value="">{t('adminOrders.selectStatus')}</option>
                            {paymentOptions.map((st) => (
                              <option key={st} value={st}>
                                {statusLabel(st, t)}
                              </option>
                            ))}
                          </select>
                          <textarea
                            rows={2}
                            className="w-full rounded border border-brand-700/35 bg-surface px-2 py-1 text-xs text-brand-950"
                            placeholder={t('orders.changeCommentPlaceholder')}
                            value={commentPayment[order.id] ?? ''}
                            onChange={(e) =>
                              setCommentPayment((c) => ({ ...c, [order.id]: e.target.value }))
                            }
                            disabled={busyPay}
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleApplyPayment(order)}
                            disabled={busyPay || !selPay || selPay === order.status}
                            loading={busyPay}
                          >
                            {busyPay ? t('common.loading') : t('adminOrders.apply')}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-brand-400">—</span>
                      )}
                    </td>
                    <td className="min-w-[220px] px-3 py-2 align-top">
                      {order.status !== 'PAGADA' ? (
                        <span className="text-xs text-brand-400">{t('adminOrders.fulfillmentOnlyWhenPaid')}</span>
                      ) : (
                        <>
                          <p className="mb-2 font-medium text-brand-950">
                            {operationalOrderStatusLabel(order.fulfillmentStatus, t)}
                          </p>
                          {nextFulfillment.length > 0 ? (
                            <div className="flex max-w-[220px] flex-col gap-1">
                              <select
                                className="rounded border border-brand-700/35 bg-surface px-2 py-1 text-brand-950"
                                value={selFul}
                                onChange={(e) =>
                                  setSelectionFulfillment((s) => ({
                                    ...s,
                                    [order.id]: (e.target.value || '') as OrderFulfillmentStatus | '',
                                  }))
                                }
                                disabled={busyFul}
                              >
                                <option value="">{t('adminOrders.selectStatus')}</option>
                                {nextFulfillment.map((st) => (
                                  <option key={st} value={st}>
                                    {operationalOrderStatusLabel(st, t)}
                                  </option>
                                ))}
                              </select>
                              <textarea
                                rows={2}
                                className="w-full rounded border border-brand-700/35 bg-surface px-2 py-1 text-xs text-brand-950"
                                placeholder={t('orders.changeCommentPlaceholder')}
                                value={commentFulfillment[order.id] ?? ''}
                                onChange={(e) =>
                                  setCommentFulfillment((c) => ({ ...c, [order.id]: e.target.value }))
                                }
                                disabled={busyFul}
                              />
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleApplyFulfillment(order)}
                                disabled={busyFul || !selFul || selFul === order.fulfillmentStatus}
                                loading={busyFul}
                              >
                                {busyFul ? t('common.loading') : t('adminOrders.apply')}
                              </Button>
                            </div>
                          ) : (
                            <span className="text-xs text-brand-400">{t('adminOrders.fulfillmentComplete')}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-brand-950">
                      {formatPrice(order.totalAmountCop, 'COP')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-brand-700">
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-xs text-brand-600 hover:text-brand-400 hover:underline"
                      >
                        {t('adminOrders.viewPublicDetail')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
